import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Typography,
    Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClipLoader from 'react-spinners/ClipLoader';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';

import '../../css/table-sort.css';

const themeColor = '#a71e3b';

function LabConsolleModifyAF(props) {
    const [dataLib, setDataLib] = useState([]);
    const [searchLib, setSearchLib] = useState("");
    const [emailOptions, setEmailOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [rowSelection, setRowSelection] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const {
        setAdSel,
        buttonLabel = "Modifica attività didattica del laboratorio",
        dialogTitle = "Modifica attività didattica del laboratorio",
    } = props;

    // Carica le opzioni email per l'autocomplete al montaggio
    useEffect(() => {
        const fetchEmailOptions = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_URL_GET_DOCEMAIL_AFS, {
                    credentials: 'include',
                    method: 'GET',
                });
                const data = await response.json();
                const emailList = data.map(item => item.docPostaElettronica);
                const uniqueEmailList = [...new Set(emailList)];
                setEmailOptions(uniqueEmailList);
            } catch (error) {
                console.error('Errore nel caricamento delle opzioni di email:', error);
            }
        };
        fetchEmailOptions();
    }, []);

    // Carica attività didattiche per email docente
    useEffect(() => {
        if (!searchLib || !showModal) return;

        const fetchData = async () => {
            try {
                const formBody = ["payload=" + JSON.stringify({ key: "", value: searchLib })];
                const resp = await fetch(process.env.REACT_APP_URL_GET_AFS_BY_KEY, {
                    credentials: 'include',
                    method: "POST",
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body: formBody
                });
                const data = await resp.json();
                setDataLib(data);
            } catch (error) {
                console.log(error, "Errore!");
            }
        };

        setDataLib([]);
        fetchData();
    }, [searchLib, showModal]);

    const columnsLib = useMemo(
        () => [
            {
                accessorKey: 'docPostaElettronica',
                header: 'E-mail Docente',
                size: 120,
            },
            {
                accessorKey: 'anaAfDescIta',
                header: 'Attività Didattica',
                size: 150,
            },
            {
                accessorKey: "anaModCod",
                header: "Codice Modulo",
                size: 120,
            },
            {
                accessorKey: "anaModDescIta",
                header: "Modulo",
                size: 120,
            },
            {
                accessorKey: "easyAd",
                header: "Codice Easy",
                size: 120,
                Cell: ({ cell }) => {
                    const val = cell.getValue();
                    if (Array.isArray(val)) return val.map(ea => ea.easyAdCod).join(', ');
                    return val || '';
                },
            },
        ],
        [],
    );

    // Gestisci selezione riga
    useEffect(() => {
        if (!showModal) return;

        const selectedRows = Object.keys(rowSelection)
            .filter(index => rowSelection[index])
            .map(index => dataLib[index]);

        const selectedAfRow = selectedRows[0];

        if (selectedAfRow && !isSaving) {
            // Gestisci setAdSel come funzione asincrona
            const handleSelection = async () => {
                setIsSaving(true);
                try {
                    await setAdSel(selectedAfRow);
                    setShowModal(false);
                    setRowSelection({});
                    setSearchLib("");
                } finally {
                    setIsSaving(false);
                }
            };
            handleSelection();
        }
    }, [rowSelection, dataLib, setAdSel, showModal, isSaving]);

    const table = useMaterialReactTable({
        columns: columnsLib,
        data: dataLib || [],
        enableGlobalFilterModes: true,
        enablePagination: true,
        enableBottomToolbar: true,
        paginationDisplayMode: 'pages',
        pageSize: 5,
        enableColumnPinning: false,
        enableRowNumbers: false,
        enableRowVirtualization: true,
        enableRowSelection: true,
        enableMultiRowSelection: false,
        enableSelectAll: false,
        onRowSelectionChange: setRowSelection,
        state: { rowSelection }
    });

    const handleOpen = () => {
        setShowModal(true);
        setRowSelection({});
    };

    return (
        <>
            <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpen}
                sx={{
                    backgroundColor: themeColor,
                    '&:hover': { backgroundColor: '#8a1830' }
                }}
            >
                {buttonLabel}
            </Button>

            <Dialog
                open={showModal}
                onClose={() => setShowModal(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{
                    fontFamily: "'PT Sans Narrow', Arial, sans-serif",
                    fontWeight: 700,
                    color: themeColor
                }}>
                    {dialogTitle}
                </DialogTitle>
                <DialogContent dividers>
                    {isSaving && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                            <ClipLoader size={35} color={themeColor} />
                            <Typography sx={{ ml: 2 }}>Salvataggio in corso...</Typography>
                        </Box>
                    )}
                    <Box sx={{ mb: 3, width: '85%' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            Ricerca per e-mail del Docente
                        </Typography>
                        <Autocomplete
                            freeSolo
                            disabled={isSaving}
                            options={emailOptions}
                            inputValue={searchLib}
                            onInputChange={(event, newInputValue) => {
                                setSearchLib(newInputValue);
                                setRowSelection({});
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    required
                                    size="small"
                                    variant="outlined"
                                    label="E-Mail"
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ overflowX: 'auto', opacity: isSaving ? 0.5 : 1, pointerEvents: isSaving ? 'none' : 'auto' }}>
                        <MaterialReactTable table={table} />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default LabConsolleModifyAF;
